import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import type { StartTopUpDto, StartUpgradeDto } from '@repo/contracts';
import { CREDIT_PACK_DETAILS } from '@repo/contracts/billing';

import { trackEvent } from '../analytics/posthog';
import { creditKeys } from '../credits/use-credits';
import {
  openBillingPortal,
  startCreditCheckout,
  startSubscriptionCheckout,
} from './billing-api';
import { useInvalidateBillingOverview } from './use-billing-overview';

export function useStartSubscription() {
  return useMutation({
    mutationFn: (dto: StartUpgradeDto) => {
      trackEvent('upgrade_started', { plan: dto.plan });
      return startSubscriptionCheckout(dto);
    },
    onError: () => {
      trackEvent('checkout_redirect_failed', { kind: 'subscription' });
    },
    onSuccess: (data) => {
      // Redirect to Stripe checkout
      window.location.href = data.url;
    },
  });
}

export function useStartCreditCheckout() {
  return useMutation({
    mutationFn: (dto: StartTopUpDto) => {
      trackEvent('checkout_started', {
        credits: CREDIT_PACK_DETAILS[dto.packSize].credits,
        pack: String(dto.packSize),
      });
      return startCreditCheckout(dto);
    },
    onError: () => {
      trackEvent('checkout_redirect_failed', { kind: 'topup' });
    },
    onSuccess: (data) => {
      // Redirect to Stripe checkout
      window.location.href = data.url;
    },
  });
}

export function useOpenBillingPortal() {
  return useMutation({
    mutationFn: (teamId?: string) => {
      trackEvent('billing_portal_opened', { team: Boolean(teamId) });
      return openBillingPortal(teamId);
    },
    onError: () => {
      trackEvent('checkout_redirect_failed', { kind: 'portal' });
    },
    onSuccess: (data) => {
      // Same tab, not `window.open`: this runs after an awaited fetch, so it
      // is outside the click's call stack and popup blockers swallow a new
      // window — the button would just say "Opening…" forever. The portal
      // session's own `return_url` is /billing, so the round trip lands back
      // here anyway.
      window.location.href = data.url;
    },
  });
}

/**
 * Handle Stripe checkout return — invalidate billing and credits after redirect.
 *
 * Called from billing-page on mount when ?session_id or ?topup query params are
 * present. The webhook fires asynchronously, so give it time to update the server
 * state before we refetch. Then strip the params so reload doesn't re-trigger.
 *
 * The caller strips them through react-router's own `setSearchParams`, not
 * `history.replaceState`: the router doesn't observe a raw replaceState, so
 * its `searchParams` would keep the stale `session_id` and the next workspace
 * switch would write it straight back into the URL. `?teamId` survives —
 * it's what the page is looking at.
 */
export function useHandleCheckoutReturn(
  stripCheckoutParams: () => void,
): () => void {
  const queryClient = useQueryClient();
  const invalidateBilling = useInvalidateBillingOverview();

  return useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('session_id') && !params.has('topup')) return;

    // Stripe's way back, read before the params are stripped. A cancelled
    // *subscription* returns to a bare /billing, so it cannot be told from a
    // plain visit and is not counted.
    const topup = params.get('topup');
    if (topup === 'cancel') {
      trackEvent('checkout_cancelled', { kind: 'topup' });
    } else {
      trackEvent('checkout_completed', {
        kind: topup === 'success' ? 'topup' : 'subscription',
      });
    }

    // Invalidate both billing and credits after webhook fulfillment
    setTimeout(() => {
      invalidateBilling();
      void queryClient.invalidateQueries({ queryKey: creditKeys.balance() });
    }, 1000);

    stripCheckoutParams();
  }, [invalidateBilling, queryClient, stripCheckoutParams]);
}
